import { useEffect, useState } from 'react';
import { Alert, StatCard, Table } from '@/design-system';
import { ProblemAlert } from '@/app/ProblemAlert';
import { api } from '@/lib/api';
import type { ApplicationStatus } from '@/lib/intake/api';
import { formatDateTime } from '@/lib/format';
import { type Problem, toProblem } from '@/lib/problem';
import { STATUS_LABELS } from '@/pages/review/api';
import styles from './OperationsPage.module.css';

interface Figures {
  applicationsByStatus: Record<ApplicationStatus, number>;
  submittedLast30Days: number;
  filesAwaitingScan: number;
  oldestAwaitingScanSince: string | null;
  filesInfected: number;
  filesUnchecked: number;
  mailWaiting: number;
  oldestMailWaitingSince: string | null;
  mailGivenUpLast30Days: number;
  retentionDays: number | null;
  draftsReminded: number;
  applicationsPurged: number;
  at: string;
}

const count = (n: number) => n.toLocaleString('en-US');

/**
 * Whether the work behind the scenes keeps up: the malware check, the mail and the
 * retention period. Every figure is counted from the database when the page loads.
 */
export function OperationsPage() {
  const [figures, setFigures] = useState<Figures | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<Figures>('/api/admin/operations')
      .then((f) => {
        if (!cancelled) setFigures(f);
      })
      .catch((error: unknown) => {
        if (!cancelled) setProblem(toProblem(error));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.intro}>
        <h1 className={styles.title}>Operations</h1>
        <p className={styles.muted}>
          Whether the malware check, the mail and the retention period are keeping up.
          {figures && <> Counted at {formatDateTime(figures.at)}.</>}
        </p>
      </div>

      {problem && <ProblemAlert problem={problem} title="The figures could not be loaded" />}

      {figures && (
        <>
          {(figures.mailGivenUpLast30Days > 0 || figures.filesUnchecked > 0) && (
            <Alert tone="danger" title="Needs attention">
              {figures.mailGivenUpLast30Days > 0 &&
                `${count(figures.mailGivenUpLast30Days)} emails could not be sent after a day of trying; check the mail settings. `}
              {figures.filesUnchecked > 0 &&
                `${count(figures.filesUnchecked)} files could not be checked for malware; the scanner may be down.`}
            </Alert>
          )}
          <section aria-labelledby="work" className={styles.grid}>
            <h2 id="work" className={styles.visuallyHidden}>
              Work in hand
            </h2>
            <StatCard label="Sent in the last 30 days" value={count(figures.submittedLast30Days)} />
            <StatCard label="Files waiting for the malware check" value={count(figures.filesAwaitingScan)} />
            <StatCard label="Emails waiting to be sent" value={count(figures.mailWaiting)} />
            <StatCard label="Files found to carry malware" value={count(figures.filesInfected)} />
          </section>

          <section aria-labelledby="queues">
            <h2 id="queues" className={styles.sectionTitle}>
              Queues
            </h2>
            <Table
              caption="The oldest work still waiting"
              columns={[
                { key: 'what', header: 'Waiting' },
                { key: 'since', header: 'Oldest since', nowrap: true },
              ]}
              rows={[
                {
                  key: 'scan',
                  cells: {
                    what: 'Malware check',
                    since: figures.oldestAwaitingScanSince ? formatDateTime(figures.oldestAwaitingScanSince) : 'Nothing waiting',
                  },
                },
                {
                  key: 'mail',
                  cells: {
                    what: 'Mail',
                    since: figures.oldestMailWaitingSince ? formatDateTime(figures.oldestMailWaitingSince) : 'Nothing waiting',
                  },
                },
              ]}
            />
          </section>

          <section aria-labelledby="applications">
            <h2 id="applications" className={styles.sectionTitle}>
              Applications by status
            </h2>
            <Table
              caption="Applications by status, drafts included"
              columns={[
                { key: 'status', header: 'Status' },
                { key: 'count', header: 'Applications', numeric: true },
              ]}
              rows={(Object.keys(figures.applicationsByStatus) as ApplicationStatus[]).map((s) => ({
                key: s,
                cells: { status: STATUS_LABELS[s], count: count(figures.applicationsByStatus[s]) },
              }))}
            />
          </section>

          <section aria-labelledby="retention">
            <h2 id="retention" className={styles.sectionTitle}>
              Retention
            </h2>
            <p className={styles.text}>
              {figures.retentionDays === null
                ? 'No retention period is set, so closed applications are kept. Set RETENTION_DAYS once the period is decided.'
                : `Closed applications are deleted ${count(figures.retentionDays)} days after closing.`}{' '}
              Drafts untouched for 150 days are reminded, and deleted at 180.
            </p>
            <p className={styles.muted}>
              {count(figures.draftsReminded)} drafts reminded and not yet changed; {count(figures.applicationsPurged)}{' '}
              applications deleted so far.
            </p>
          </section>
        </>
      )}
    </div>
  );
}
